import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate } from "../lib/format";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, CircleDashed, Download, ExternalLink, FileCode, FileText, FlaskConical,
  Plus, RefreshCw, Send, ShieldCheck, Trash2, Upload, XCircle, ChevronDown, ChevronRight, Package,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const inputCls = "w-full px-3 py-2 border border-border rounded-sm text-sm bg-background";
const inputAuto = inputCls.replace("w-full", "w-auto");
const CARD = "bg-card border border-border rounded-md";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-60";
const BTN_GHOST = "inline-flex items-center gap-1 border border-border hover:bg-muted text-foreground rounded-sm px-2.5 py-1.5 text-[13px] disabled:opacity-50";

const TIPOS = {
  33: "Factura electrónica", 34: "Factura exenta", 39: "Boleta electrónica", 41: "Boleta exenta",
  46: "Factura de compra", 52: "Guía de despacho", 56: "Nota de débito", 61: "Nota de crédito",
};
const URL_FOLIOS = "https://maullin.sii.cl/cvc_cgi/dte/of_solicita_folios";
const URL_INSTRUCTIVO = "https://www.sii.cl/ayudas/ayudas_por_servicios/2004-instructivo-2007.html";
const URL_BOL_GUIA = "https://www.sii.cl/servicios_online/1039-guia_emitir_boleta_servicio-1184.html";
const URL_BOL_SET = "https://www4.sii.cl/certBolElectDteInternet/?SET=1";
const URL_BOL_REVISION = "https://www4.sii.cl/certBolElectDteInternet/?SET=2";
const URL_BOL_DECLARACION = "https://www4.sii.cl/certBolElectDteInternet/";
const URL_USUARIOS = "https://maullin.sii.cl/dte/mn_adm_cont.html";
const URL_POSTULANTES = "https://maullin.sii.cl/cvc/dte/certificacion_dte.html";
// Rechazos del envío completo: se puede reenviar el mismo archivo (sin gastar folios)
const REENVIABLES = ["106", "RFR", "RCT", "RSC", "RCH"];
const PERMISO_RE = /sin permiso|no autorizado a firmar/i;
// Rechazos totales (incluye libros): la generación se puede borrar y rehacer con los mismos folios
const RECHAZO_TOTAL = [...REENVIABLES, "LRH", "LRF", "LRS", "LRC", "LNC"];
const esPermiso = (e) => !!e && (e.estado === "106" || [e.glosa, ...(e.errores || [])].some((t) => PERMISO_RE.test(t || "")));
const borrable = (g) => g.prueba || g.archivos.every((a) => !a.track_id || RECHAZO_TOTAL.includes(a.estado_sii?.estado));
const VACIO = { rut: "", razon_social: "", giro: "", direccion: "", comuna: "", ciudad: "" };

async function abrirBlob(url) {
  const res = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(res.data);
  window.open(u, "_blank");
  setTimeout(() => URL.revokeObjectURL(u), 60000);
}

async function descargarBlob(url, nombre) {
  const res = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = u; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 60000);
}

function Paso({ n, titulo, estado, children, accion }) {
  const icono = estado === "ok"
    ? <CheckCircle2 className="h-5 w-5 text-success" />
    : estado === "error"
      ? <AlertTriangle className="h-5 w-5 text-warning" />
      : <CircleDashed className="h-5 w-5 text-muted-foreground" />;
  return (
    <section className={`${CARD} p-4 sm:p-5`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          {icono}
          <h3 className="text-[15px] font-semibold">
            <span className="text-muted-foreground mr-1.5">{n}.</span>{titulo}
          </h3>
        </div>
        {accion}
      </div>
      {children}
    </section>
  );
}

export default function CertificacionSII({ editable, onIrConfig }) {
  const [proceso, setProceso] = useState("factura");
  const [st, setSt] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [borradorSim, setBorradorSim] = useState(null);

  const load = async (p = proceso) => {
    try {
      const { data } = await api.get("/facturador/certificacion", { params: { proceso: p } });
      if (data.proceso === p) setSt(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => {
    setCargando(true);
    setSt(null);
    setBorradorSim(null);
    load(proceso);
  }, [proceso]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCargado = (data) => {
    if (data?.proceso && data.proceso !== proceso) {
      toast.info(data.proceso === "boleta"
        ? "El archivo es el set de boletas: se abrió la certificación de boleta electrónica"
        : "El archivo es el set de facturas: se abrió la certificación de factura electrónica");
      setProceso(data.proceso);
    } else {
      load();
    }
  };

  const esBoleta = proceso === "boleta";
  const esSimulacion = proceso === "simulacion";
  const esIntercambio = proceso === "intercambio";
  const selector = (
    <div className="inline-flex border border-border rounded-sm overflow-hidden text-[13px]" data-testid="cert-proceso">
      {[["factura", "Factura electrónica"], ["simulacion", "Simulación"], ["intercambio", "Intercambio"],
        ["boleta", "Boleta electrónica"]].map(([k, label]) => (
        <button key={k} onClick={() => setProceso(k)}
          className={`px-3 py-1.5 ${proceso === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
          {label}
        </button>
      ))}
    </div>
  );

  const intro = (
    <div className={`${CARD} p-4 sm:p-5 text-[13px] leading-relaxed`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="font-semibold text-[15px]">
          Asistente de certificación SII — {esBoleta ? "Boleta electrónica" : esSimulacion ? "Simulación"
            : esIntercambio ? "Intercambio de información" : "Factura electrónica"}
        </div>
        {selector}
      </div>
      {esIntercambio ? (
        <p className="text-muted-foreground">
          Etapa en que la empresa actúa como receptora: el SII le entrega un envío de documentos (EnvioDTE) como si lo
          hubiera emitido un proveedor. El sistema lo valida (esquema, firmas, RUT receptor) y genera las tres respuestas
          firmadas: la recepción del envío, el recibo de mercaderías o servicios (Ley 19.983) y la aprobación comercial de
          cada documento. Esos archivos se suben a mano en el sitio de postulantes del SII.
        </p>
      ) : esSimulacion ? (
        <p className="text-muted-foreground">
          Etapa siguiente al set de pruebas: arme usted mismo documentos representativos de su operación real (facturas,
          notas de crédito y débito, guías, facturas exentas), con sus clientes, productos y precios. No dependen del set
          del SII ni llevan su referencia. Se timbran y firman en un solo sobre que se envía a Maullín. Estos documentos no
          se registran en su contabilidad.
        </p>
      ) : esBoleta ? (
        <p className="text-muted-foreground">
          Cargue el set de pruebas de boletas que entrega el SII y el sistema arma cada caso (precios con IVA incluido,
          ítems exentos y unidades según las observaciones), los timbra y firma en un sobre EnvioBOLETA, genera el
          Reporte de Consumo de Folios (RCOF) del día y las boletas impresas. Todo se valida contra los esquemas oficiales
          antes de enviarlo. Estas boletas no se registran en su contabilidad.
        </p>
      ) : (
        <p className="text-muted-foreground">
          Cargue el set de pruebas que entrega el SII y el sistema arma todos los casos, los timbra y firma, genera los
          libros de ventas, compras y guías, y las muestras impresas (ejemplar tributario y cedible). Todo se valida contra
          los esquemas oficiales antes de enviarlo a Maullín. Estos documentos no se registran en su contabilidad.
        </p>
      )}
    </div>
  );

  if (cargando || !st || st.proceso !== proceso) {
    return (
      <div className="space-y-5" data-testid="certificacion-sii">
        {intro}
        <div className={`${CARD} p-5 text-sm text-muted-foreground`}>Cargando…</div>
      </div>
    );
  }

  const folios = esSimulacion && borradorSim ? foliosSimulacion(borradorSim, st.cafs) : st.folios;
  const stVista = { ...st, folios };
  const foliosOk = Object.keys(folios || {}).length > 0
    && Object.values(folios).every((f) => f.disponibles >= f.requeridos);
  const requisitosOk = st.faltan_emisor.length === 0 && st.certificado.configurado && foliosOk;

  if (esIntercambio) {
    return (
      <div className="space-y-5" data-testid="certificacion-sii">
        {intro}
        <Intercambio st={st} editable={editable} onCambio={() => load()} onIrConfig={onIrConfig} />
        <Generaciones st={st} editable={editable} onCambio={() => load()} manual />
        <SiguientesPasosIntercambio />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="certificacion-sii">
      {intro}
      <Requisitos st={stVista} ok={requisitosOk} foliosOk={foliosOk} onIrConfig={onIrConfig} />
      {!esSimulacion && <SetPruebas st={st} editable={editable} onCargado={setCargado} boleta={esBoleta} />}
      {esSimulacion ? (
        <>
          <Configuracion st={st} editable={editable} onGuardado={() => load()} simulacion />
          <Simulacion st={stVista} editable={editable} requisitosOk={requisitosOk} onCambio={() => load()}
            onBorrador={setBorradorSim} />
          <Generaciones st={st} editable={editable} onCambio={() => load()} />
          <SiguientesPasosSimulacion />
        </>
      ) : esBoleta ? (
        <>
          <ConfiguracionBoleta st={st} editable={editable} onGuardado={() => load()} />
          <GenerarBoleta st={st} editable={editable} requisitosOk={requisitosOk} onGenerado={() => load()} />
          <Generaciones st={st} editable={editable} onCambio={() => load()} />
          <SiguientesPasosBoleta />
        </>
      ) : (
        <>
          <Configuracion st={st} editable={editable} onGuardado={() => load()} />
          <Generar st={st} editable={editable} requisitosOk={requisitosOk} onGenerado={() => load()} />
          <Generaciones st={st} editable={editable} onCambio={() => load()} />
          <SiguientesPasos />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Requisitos({ st, ok, foliosOk, onIrConfig }) {
  const tipos = Object.entries(st.folios || {});
  return (
    <Paso n={1} titulo="Requisitos" estado={ok ? "ok" : "error"}>
      <div className="grid md:grid-cols-3 gap-3 text-[13px]">
        <div className="border border-border rounded-sm p-3">
          <div className="label-caps mb-1.5">Datos del emisor</div>
          <div className="font-medium">{st.emisor.nombre}</div>
          <div className="font-mono text-muted-foreground">{st.emisor.rut}</div>
          {st.faltan_emisor.length ? (
            <div className="text-warning mt-1.5 flex gap-1">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Falta: {st.faltan_emisor.join(", ")}.{" "}
                <button className="underline" onClick={onIrConfig}>Completar</button></span>
            </div>
          ) : (
            <div className="text-success mt-1.5 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Completo</div>
          )}
        </div>
        <div className="border border-border rounded-sm p-3">
          <div className="label-caps mb-1.5">Certificado digital</div>
          {st.certificado.configurado ? (
            <>
              <div className="flex items-center gap-1 text-success"><ShieldCheck className="h-3.5 w-3.5" /> {st.certificado.titular}</div>
              <div className="font-mono text-muted-foreground">RUT firmante: {st.certificado.rut || "—"}</div>
              <div className="text-muted-foreground">Vence: {fmtDate(st.certificado.valido_hasta)}</div>
            </>
          ) : (
            <div className="text-warning flex gap-1">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Pendiente. Cárguelo en <button className="underline" onClick={onIrConfig}>Configuración</button>.
                Mientras tanto puede generar una vista previa.</span>
            </div>
          )}
        </div>
        <div className="border border-border rounded-sm p-3">
          <div className="label-caps mb-1.5">Folios de certificación (CAF)</div>
          {tipos.length === 0 ? (
            <div className="text-muted-foreground">
              {st.proceso === "simulacion" ? "Se calculan según los documentos de la simulación." : "Se calculan al cargar el set de pruebas."}
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <tbody>
                {tipos.map(([t, f]) => (
                  <tr key={t}>
                    <td className="py-0.5">{t} · {TIPOS[t] || ""}</td>
                    <td className={`text-right font-mono ${f.disponibles >= f.requeridos ? "text-success" : "text-warning"}`}>
                      {f.disponibles}/{f.requeridos}
                      {f.disponibles < f.requeridos && <span className="font-sans"> (faltan {f.requeridos - f.disponibles})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!foliosOk && tipos.length > 0 && (
            <a href={URL_FOLIOS} target="_blank" rel="noreferrer" className="text-[12px] underline inline-flex items-center gap-1 mt-1.5">
              Solicitar folios en Maullín <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <div className="text-[11px] text-muted-foreground mt-1">
            Disponibles/necesarios, sumando todos los CAF de certificación del tipo.{" "}
            {st.proceso === "simulacion"
              ? "Los necesarios son los documentos de su lista (paso 3): si le faltan folios de un tipo, quite documentos de ese tipo."
              : "Cárguelos en Configuración → Folios (CAF). Pida algunos extra por si debe reenviar."}
          </div>
        </div>
      </div>
    </Paso>
  );
}

// ---------------------------------------------------------------------------
function SetPruebas({ st, editable, onCargado, boleta }) {
  const [subiendo, setSubiendo] = useState(false);
  const [pegar, setPegar] = useState(false);
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(null);
  const set = st.set;

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facturador/certificacion/set", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Set de pruebas cargado");
      onCargado(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubiendo(false);
    }
  };

  const guardarTexto = async () => {
    setSubiendo(true);
    try {
      const { data } = await api.post("/facturador/certificacion/set-texto", { texto });
      toast.success("Set de pruebas cargado");
      setPegar(false); setTexto("");
      onCargado(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubiendo(false);
    }
  };

  const accion = editable && (
    <div className="flex gap-2">
      <label className={`${BTN_GHOST} cursor-pointer`}>
        <Upload className="h-3.5 w-3.5" /> {subiendo ? "Cargando…" : set ? "Reemplazar .txt" : "Subir .txt del SII"}
        <input type="file" accept=".txt,text/plain" className="hidden" onChange={subir} disabled={subiendo} />
      </label>
      <button className={BTN_GHOST} onClick={() => setPegar(!pegar)}>Pegar texto</button>
    </div>
  );

  return (
    <Paso n={2} titulo="Set de pruebas" estado={set ? "ok" : "pendiente"} accion={accion}>
      {pegar && (
        <div className="mb-3 space-y-2">
          <textarea className={`${inputCls} font-mono text-[12px] h-40`} value={texto} onChange={(e) => setTexto(e.target.value)}
            placeholder="Pegue aquí el contenido completo del set de pruebas…" />
          <button className={BTN_PRIMARY} disabled={subiendo || texto.length < 20} onClick={guardarTexto}>Procesar</button>
        </div>
      )}
      {!set && boleta ? (
        <p className="text-[13px] text-muted-foreground">
          El representante legal solicita el set en el{" "}
          <a href={URL_BOL_SET} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
            sitio de certificación de boletas del SII <ExternalLink className="h-3 w-3" />
          </a>{" "}
          (debe estar habilitado en el ambiente de certificación). Súbalo tal cual: los nombres de los ítems se usan
          exactamente como vienen.
        </p>
      ) : !set ? (
        <p className="text-[13px] text-muted-foreground">
          Descargue el set desde Maullín (Set de pruebas → Generar/Ver set) y súbalo tal cual. Los nombres de los ítems se
          usan exactamente como vienen (con tildes y ñ), como exige el SII.
        </p>
      ) : boleta ? (
        <CasosBoleta set={set} />
      ) : (
        <div className="space-y-2 text-[13px]">
          <div className="text-muted-foreground">
            {set.nombre_archivo} · cargado {fmtDate(set.cargado_at)}
          </div>
          {set.advertencias.map((a) => (
            <div key={a} className="text-warning flex gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {a}</div>
          ))}
          <div className="divide-y divide-border border border-border rounded-sm">
            {set.sets.map((s) => (
              <div key={s.clave + s.atencion}>
                <button className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 text-left"
                  onClick={() => setAbierto(abierto === s.clave ? null : s.clave)}>
                  <span className="flex items-center gap-2">
                    {(s.casos || s.compras) ? (abierto === s.clave ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="w-3.5" />}
                    <span className="font-medium">{s.titulo}</span>
                    <span className="text-muted-foreground font-mono text-[12px]">N° atención {s.atencion}</span>
                  </span>
                  <span className="text-[12px] text-muted-foreground">
                    {s.casos ? `${s.casos.length} casos` : s.compras ? `${s.compras.length} documentos` : ""}
                    {!s.soportado && <span className="text-warning ml-2">no soportado</span>}
                  </span>
                </button>
                {abierto === s.clave && s.casos && (
                  <table className="w-full text-[12px] mb-2">
                    <thead className="text-muted-foreground">
                      <tr><th className="text-left px-3 py-1">Caso</th><th className="text-left">Documento</th><th className="text-left">Referencia</th><th className="text-right px-3">Ítems</th></tr>
                    </thead>
                    <tbody>
                      {s.casos.map((c) => (
                        <tr key={c.caso} className="border-t border-border/60">
                          <td className="px-3 py-1 font-mono">{c.caso}</td>
                          <td>{c.tipo_dte ? `${c.tipo_dte} · ` : ""}{c.documento}</td>
                          <td className="text-muted-foreground">{c.referencia ? `${c.referencia} · ${c.razon || ""}` : "—"}</td>
                          <td className="text-right px-3">{c.items}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {abierto === s.clave && s.compras && (
                  <table className="w-full text-[12px] mb-2">
                    <thead className="text-muted-foreground">
                      <tr><th className="text-left px-3 py-1">Documento</th><th className="text-left">Folio</th><th className="text-left">Observación</th><th className="text-right">Exento</th><th className="text-right px-3">Afecto</th></tr>
                    </thead>
                    <tbody>
                      {s.compras.map((c) => (
                        <tr key={`${c.tipo}-${c.folio}`} className="border-t border-border/60">
                          <td className="px-3 py-1">{c.tipo} · {c.documento}</td>
                          <td className="font-mono">{c.folio}</td>
                          <td className="text-muted-foreground">{c.observacion}</td>
                          <td className="text-right font-mono">{c.exento ? fmtCLP(c.exento) : "—"}</td>
                          <td className="text-right font-mono px-3">{c.afecto ? fmtCLP(c.afecto) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Paso>
  );
}

function CasosBoleta({ set }) {
  return (
    <div className="space-y-2 text-[13px]">
      <div className="text-muted-foreground">
        {set.nombre_archivo} · cargado {fmtDate(set.cargado_at)} · {set.casos.length} casos
        {set.exenta ? " · boletas exentas (41)" : " · boletas afectas (39)"}
      </div>
      {set.advertencias.map((a) => (
        <div key={a} className="text-warning flex gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {a}</div>
      ))}
      <div className="border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="text-muted-foreground bg-muted/40">
            <tr>
              <th className="text-left px-3 py-1.5">Caso</th>
              <th className="text-left">Ítems (cantidad × precio con IVA)</th>
              <th className="text-right px-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {set.casos.map((c) => (
              <tr key={c.caso} className="border-t border-border/60 align-top">
                <td className="px-3 py-1.5 font-mono whitespace-nowrap">{c.caso}</td>
                <td className="py-1.5">
                  {c.items.map((it, i) => (
                    <div key={i}>
                      {it.nombre} <span className="text-muted-foreground font-mono">{it.cantidad}{it.unidad ? ` ${it.unidad}` : ""} × {fmtCLP(it.precio)}</span>
                      {it.exento && <span className="ml-1.5 text-[10px] px-1 rounded-sm bg-muted text-muted-foreground">EXENTO</span>}
                    </div>
                  ))}
                  {c.observacion && <div className="text-[11px] text-muted-foreground italic">{c.observacion}</div>}
                </td>
                <td className="text-right font-mono px-3 py-1.5">
                  {fmtCLP(c.items.reduce((s, it) => s + Math.round((Number(it.cantidad) || 1) * (Number(it.precio) || 0)), 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Cada boleta referencia su caso (CodRef «SET», RazonRef «CASO-n») y se emite al receptor genérico 66.666.666-6.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ListaContactos({ titulo, ayuda, items, setItems, editable, origen }) {
  const [catalogo, setCatalogo] = useState(null);

  const importar = async () => {
    try {
      const { data } = await api.get(origen);
      setCatalogo((data || []).filter((c) => c.rut));
    } catch (e) {
      toast.error(extractError(e));
    }
  };
  const agregarDesde = (c) => {
    if (items.some((i) => i.rut.replace(/\./g, "").toUpperCase() === c.rut.replace(/\./g, "").toUpperCase())) return;
    setItems([...items, {
      rut: c.rut, razon_social: c.razon_social || "", giro: c.giro || "", direccion: c.direccion || "",
      comuna: c.comuna || "", ciudad: c.ciudad || "",
    }]);
  };
  const cambiar = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps">{titulo}</div>
          <div className="text-[11px] text-muted-foreground">{ayuda}</div>
        </div>
        {editable && (
          <div className="flex gap-2">
            <button type="button" className={BTN_GHOST} onClick={importar}>Desde mis registros</button>
            <button type="button" className={BTN_GHOST} onClick={() => setItems([...items, { ...VACIO }])}>
              <Plus className="h-3.5 w-3.5" /> Agregar
            </button>
          </div>
        )}
      </div>
      {catalogo && (
        <div className="border border-border rounded-sm max-h-40 overflow-auto text-[12px]">
          {catalogo.length === 0 && <div className="p-2 text-muted-foreground">No hay registros con RUT.</div>}
          {catalogo.map((c) => (
            <button type="button" key={c.id || c.rut} onClick={() => agregarDesde(c)}
              className="w-full text-left px-2 py-1 hover:bg-muted flex justify-between">
              <span>{c.razon_social}</span><span className="font-mono text-muted-foreground">{c.rut}</span>
            </button>
          ))}
        </div>
      )}
      {items.length === 0 && <div className="text-[12px] text-warning">Sin registros.</div>}
      {items.map((c, i) => (
        <div key={i} className="grid grid-cols-6 lg:grid-cols-12 gap-1.5 pb-1.5 border-b border-border lg:border-0 lg:pb-0">
          <input className={`${inputCls} col-span-3 lg:col-span-2 font-mono`} placeholder="RUT" value={c.rut} disabled={!editable}
            onChange={(e) => cambiar(i, "rut", e.target.value)} />
          <input className={`${inputCls} col-span-3`} placeholder="Razón social" value={c.razon_social} disabled={!editable}
            onChange={(e) => cambiar(i, "razon_social", e.target.value)} />
          <input className={`${inputCls} col-span-2`} placeholder="Giro" value={c.giro || ""} disabled={!editable}
            onChange={(e) => cambiar(i, "giro", e.target.value)} />
          <input className={`${inputCls} col-span-2`} placeholder="Dirección" value={c.direccion || ""} disabled={!editable}
            onChange={(e) => cambiar(i, "direccion", e.target.value)} />
          <input className={`${inputCls} ${editable ? "col-span-1" : "col-span-2"} lg:col-span-2`} placeholder="Comuna" value={c.comuna || ""} disabled={!editable}
            onChange={(e) => cambiar(i, "comuna", e.target.value)} />
          {editable && (
            <button type="button" className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center"
              onClick={() => setItems(items.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Configuracion({ st, editable, onGuardado, simulacion = false }) {
  const [cfg, setCfg] = useState(st.config);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setCfg(st.config); }, [st.config]);

  const facturas = useMemo(() => {
    let n = 0;
    for (const s of st.set?.sets || []) {
      n = Math.max(n, (s.casos || []).filter((c) => !c.referencia).length);
    }
    return n;
  }, [st.set]);

  const completo = cfg.fch_resol && cfg.receptores.length > 0 && (simulacion || cfg.proveedores.length > 0);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put("/facturador/certificacion/config", {
        ...cfg,
        folio_factura_guia: cfg.folio_factura_guia ? Number(cfg.folio_factura_guia) : null,
        folios_notificacion: Object.fromEntries(Object.entries(cfg.folios_notificacion).map(([k, v]) => [k, Number(v) || 1])),
      });
      toast.success("Datos guardados");
      onGuardado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paso n={simulacion ? 2 : 3} titulo="Datos para los documentos" estado={completo ? "ok" : "pendiente"}
      accion={editable && <button className={BTN_PRIMARY} onClick={guardar} disabled={guardando}>{guardando ? "Guardando…" : "Guardar"}</button>}>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="label-caps block mb-1">Fecha de resolución (certificación)</label>
            <input type="date" className={inputCls} value={cfg.fch_resol || ""} disabled={!editable}
              onChange={(e) => setCfg({ ...cfg, fch_resol: e.target.value })} />
            <div className="text-[11px] text-muted-foreground mt-0.5">La publicada en los datos de su empresa en Maullín. N° resolución: 0.</div>
          </div>
          <div>
            <label className="label-caps block mb-1">Unidad SII</label>
            <input className={inputCls} placeholder="Ej. SANTIAGO ORIENTE" value={cfg.unidad_sii || ""} disabled={!editable}
              onChange={(e) => setCfg({ ...cfg, unidad_sii: e.target.value.toUpperCase() })} />
            <div className="text-[11px] text-muted-foreground mt-0.5">Se imprime bajo el recuadro rojo.</div>
          </div>
          {!simulacion && (<>
          <div>
            <label className="label-caps block mb-1">Folio notificación (ventas / compras / guías)</label>
            <div className="grid grid-cols-3 gap-1.5">
              {["libro_ventas", "libro_compras", "libro_guias"].map((k) => (
                <input key={k} type="number" min="1" className={`${inputCls} font-mono`} disabled={!editable}
                  value={cfg.folios_notificacion?.[k] ?? ""}
                  onChange={(e) => setCfg({ ...cfg, folios_notificacion: { ...cfg.folios_notificacion, [k]: e.target.value } })} />
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Sube solo al enviar un libro: cada reenvío necesita un folio nuevo.</div>
          </div>
          <div>
            <label className="label-caps block mb-1">Folio de la factura de la guía (libro de guías)</label>
            <input type="number" min="1" className={`${inputCls} font-mono`} placeholder="Automático" disabled={!editable}
              value={cfg.folio_factura_guia ?? ""} onChange={(e) => setCfg({ ...cfg, folio_factura_guia: e.target.value })} />
          </div>
          </>)}
        </div>
        <ListaContactos
          titulo="Receptores (clientes)"
          ayuda={simulacion
            ? "Clientes que podrá elegir en cada documento de la simulación. Use clientes reales con su giro y dirección."
            : `El SII pide RUT distintos para las distintas facturas${facturas ? `: se recomiendan al menos ${facturas}` : ""}. Use clientes reales con su giro y dirección.`}
          items={cfg.receptores} setItems={(r) => setCfg({ ...cfg, receptores: r })} editable={editable} origen="/clientes" />
        {!simulacion && (
          <ListaContactos
            titulo="Proveedores (libro de compras)"
            ayuda="Emisores de los documentos del set de compras. Deben ser RUT válidos."
            items={cfg.proveedores} setItems={(r) => setCfg({ ...cfg, proveedores: r })} editable={editable} origen="/proveedores" />
        )}
      </div>
    </Paso>
  );
}

// ---------------------------------------------------------------------------
function Generar({ st, editable, requisitosOk, onGenerado }) {
  const soportados = (st.set?.sets || []).filter((s) => s.soportado);
  const [sets, setSets] = useState(null);
  const [generando, setGenerando] = useState(null);
  const [confirmar, setConfirmar] = useState(false);
  const elegidos = sets ?? soportados.map((s) => s.clave);

  const generar = async (prueba) => {
    setGenerando(prueba ? "prueba" : "real");
    try {
      await api.post("/facturador/certificacion/generar", { sets: elegidos, prueba });
      toast.success(prueba ? "Vista previa generada" : "Set generado y firmado");
      onGenerado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGenerando(null);
      setConfirmar(false);
    }
  };

  const listo = st.set && st.config.receptores.length > 0 && st.config.fch_resol;

  return (
    <Paso n={4} titulo="Generar documentos y libros" estado={st.generaciones.some((g) => !g.prueba) ? "ok" : "pendiente"}>
      {!st.set ? (
        <p className="text-[13px] text-muted-foreground">Primero cargue el set de pruebas.</p>
      ) : (
        <div className="space-y-3 text-[13px]">
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {soportados.map((s) => (
              <label key={s.clave} className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={elegidos.includes(s.clave)} disabled={!editable}
                  onChange={(e) => setSets(e.target.checked ? [...elegidos, s.clave] : elegidos.filter((x) => x !== s.clave))} />
                {s.titulo}
              </label>
            ))}
          </div>
          <div className="text-[12px] text-muted-foreground">
            Fecha de emisión: <b>{fmtDate(st.fecha_hoy)}</b> (el SII exige la fecha del día). Los documentos van en un solo
            sobre, en el orden del set; cada libro va en su propio archivo. Si solo marca libros, se usan los documentos de la
            última generación.
          </div>
          <div className="text-[12px] text-muted-foreground">
            Orden recomendado: envíe primero el sobre con los documentos y espere el correo del SII que aprueba cada set.
            El libro de guías se rechaza si el set de guías de despacho no está aprobado; después genere solo los libros.
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button className={BTN_GHOST} disabled={!listo || !!generando} onClick={() => generar(true)}>
                <FlaskConical className="h-3.5 w-3.5" /> {generando === "prueba" ? "Generando…" : "Vista previa (sin certificado)"}
              </button>
              <button className={BTN_PRIMARY} disabled={!listo || !requisitosOk || !!generando} onClick={() => setConfirmar(true)}>
                <ShieldCheck className="h-4 w-4" /> {generando === "real" ? "Generando…" : "Generar para enviar al SII"}
              </button>
            </div>
          )}
          {!requisitosOk && (
            <div className="text-[12px] text-muted-foreground">
              Para generar los archivos definitivos necesita el certificado digital y los folios de certificación (paso 1).
            </div>
          )}
        </div>
      )}
      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generar set para el SII</AlertDialogTitle>
            <AlertDialogDescription>
              Se consumirán folios de certificación ({Object.entries(st.folios || {}).map(([t, f]) => `${f.requeridos} del tipo ${t}`).join(", ")}).
              Los documentos quedan firmados con su certificado y listos para enviar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => generar(false)}>Generar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Paso>
  );
}

// ---------------------------------------------------------------------------
function ConfiguracionBoleta({ st, editable, onGuardado }) {
  const [cfg, setCfg] = useState(st.config);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setCfg(st.config); }, [st.config]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put("/facturador/certificacion/config-boleta", cfg);
      toast.success("Datos guardados");
      onGuardado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paso n={3} titulo="Datos para las boletas" estado={cfg.fch_resol ? "ok" : "pendiente"}
      accion={editable && <button className={BTN_PRIMARY} onClick={guardar} disabled={guardando}>{guardando ? "Guardando…" : "Guardar"}</button>}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Fecha de resolución (certificación)</label>
          <input type="date" className={inputCls} value={cfg.fch_resol || ""} disabled={!editable}
            onChange={(e) => setCfg({ ...cfg, fch_resol: e.target.value })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">La publicada en los datos de su empresa en Maullín. N° resolución: 0.</div>
        </div>
        <div>
          <label className="label-caps block mb-1">Unidad SII</label>
          <input className={inputCls} placeholder="Ej. SANTIAGO ORIENTE" value={cfg.unidad_sii || ""} disabled={!editable}
            onChange={(e) => setCfg({ ...cfg, unidad_sii: e.target.value.toUpperCase() })} />
          <div className="text-[11px] text-muted-foreground mt-0.5">Se imprime bajo el recuadro rojo de la boleta.</div>
        </div>
      </div>
    </Paso>
  );
}

function GenerarBoleta({ st, editable, requisitosOk, onGenerado }) {
  const [generando, setGenerando] = useState(null);
  const [confirmar, setConfirmar] = useState(false);
  const listo = st.set && st.config.fch_resol;

  const generar = async (prueba) => {
    setGenerando(prueba ? "prueba" : "real");
    try {
      await api.post("/facturador/certificacion/generar", { proceso: "boleta", prueba });
      toast.success(prueba ? "Vista previa generada" : "Boletas generadas y firmadas");
      onGenerado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGenerando(null);
      setConfirmar(false);
    }
  };

  const diasBoletas = st.dias_boletas || [];
  const [fechaRcof, setFechaRcof] = useState(diasBoletas[0]?.fecha || st.fecha_hoy);
  const [secRcof, setSecRcof] = useState("");
  const diaRcof = diasBoletas.find((d) => d.fecha === fechaRcof);
  const generarRcof = async () => {
    setGenerando("rcof");
    try {
      const { data } = await api.post("/facturador/certificacion/generar", {
        proceso: "boleta", solo_rcof: true, fecha_rcof: fechaRcof, sec_envio_rcof: secRcof ? Number(secRcof) : null,
      });
      setSecRcof("");
      toast.success(`RCOF del ${fmtDate(data.fecha)} generado (secuencia ${data.sec_envio}): informa ${data.boletas_informadas} boleta(s). Envíelo en el paso 5.`);
      onGenerado();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGenerando(null);
    }
  };

  const soloRcof = (
    <div className="border-t border-border pt-3 mt-3 space-y-2 text-[13px]">
      <div className="text-[12px] text-muted-foreground">
        <b>Solo el RCOF, sin emitir boletas:</b> arma un Reporte de Consumo de Folios nuevo con las boletas ya generadas
        ese día (o en cero si no hay) y la siguiente secuencia de envío. Sirve para corregir un RCOF rechazado o reparado
        o informar un día sin movimiento. No consume folios. <b>La fecha debe ser la de emisión de las boletas.</b>
      </div>
      {diasBoletas.length > 0 && (
        <div className="text-[12px] flex flex-wrap gap-x-3 gap-y-1">
          <span className="text-muted-foreground">Días con boletas:</span>
          {diasBoletas.map((d) => (
            <button key={d.fecha} type="button" className={`underline ${d.fecha === fechaRcof ? "font-semibold" : ""}`}
              onClick={() => setFechaRcof(d.fecha)}>
              {fmtDate(d.fecha)} ({d.boletas} boleta{d.boletas === 1 ? "" : "s"})
            </button>
          ))}
        </div>
      )}
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" className={inputAuto} value={fechaRcof || ""} max={st.fecha_hoy}
            onChange={(e) => setFechaRcof(e.target.value)} data-testid="cert-rcof-fecha" />
          <input type="number" min={1} max={9999} className={`${inputAuto} w-28`} value={secRcof}
            placeholder={`Secuencia ${diaRcof?.sec_siguiente || 1}`} title="Secuencia de envío"
            onChange={(e) => setSecRcof(e.target.value)} data-testid="cert-rcof-secuencia" />
          <button className={BTN_GHOST} disabled={!fechaRcof || !st.config.fch_resol || !st.certificado.configurado || !!generando}
            onClick={generarRcof} data-testid="cert-solo-rcof">
            <FileCode className="h-3.5 w-3.5" /> {generando === "rcof" ? "Generando…" : "Generar solo RCOF"}
          </button>
        </div>
      )}
      {editable && fechaRcof && !diaRcof && diasBoletas.length > 0 && (
        <div className="text-[12px] text-warning">
          Ese día no tiene boletas generadas: el RCOF saldría sin movimiento. Si corresponde a las boletas del set, elija
          el día en que se generaron.
        </div>
      )}
      {editable && (
        <div className="text-[11px] text-muted-foreground">
          La secuencia se calcula sola ({diaRcof?.sec_siguiente || 1} para este día). Si el SII respondió «Secuencia de
          envío inválida… es menor o igual a N», escriba N + 1.
        </div>
      )}
      {!st.certificado.configurado && (
        <div className="text-[12px] text-muted-foreground">Necesita el certificado digital cargado.</div>
      )}
    </div>
  );

  return (
    <Paso n={4} titulo="Generar boletas y consumo de folios" estado={st.generaciones.some((g) => !g.prueba) ? "ok" : "pendiente"}>
      {!st.set ? (
        <>
          <p className="text-[13px] text-muted-foreground">Primero cargue el set de pruebas de boletas.</p>
          {soloRcof}
        </>
      ) : (
        <div className="space-y-3 text-[13px]">
          <div className="text-[12px] text-muted-foreground">
            Fecha de emisión: <b>{fmtDate(st.fecha_hoy)}</b>. Se generan las {st.set.casos.length} boletas del set en un
            solo sobre <b>EnvioBOLETA</b> y el <b>Reporte de Consumo de Folios</b> (RCOF) del día, que informa todas las
            boletas emitidas hoy. Si vuelve a generar el mismo día, el RCOF se arma con un número de secuencia nuevo.
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button className={BTN_GHOST} disabled={!listo || !!generando} onClick={() => generar(true)}>
                <FlaskConical className="h-3.5 w-3.5" /> {generando === "prueba" ? "Generando…" : "Vista previa (sin certificado)"}
              </button>
              <button className={BTN_PRIMARY} disabled={!listo || !requisitosOk || !!generando} onClick={() => setConfirmar(true)}>
                <ShieldCheck className="h-4 w-4" /> {generando === "real" ? "Generando…" : "Generar para enviar al SII"}
              </button>
            </div>
          )}
          {!requisitosOk && (
            <div className="text-[12px] text-muted-foreground">
              Para generar los archivos definitivos necesita el certificado digital y un CAF de boletas de certificación (paso 1).
            </div>
          )}
          {soloRcof}
        </div>
      )}
      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generar boletas para el SII</AlertDialogTitle>
            <AlertDialogDescription>
              Se consumirán folios de certificación ({Object.entries(st.folios || {}).map(([t, f]) => `${f.requeridos} del tipo ${t}`).join(", ")}).
              Las boletas y el RCOF quedan firmados con su certificado y listos para enviar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => generar(false)}>Generar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Paso>
  );
}

// ---------------------------------------------------------------------------
const TIPOS_SIM = [33, 34, 52, 61, 56];
const COD_REF = { 1: "Anula el documento", 2: "Corrige texto (sin montos)", 3: "Corrige montos" };
const IND_TRASLADO = {
  1: "Operación constituye venta", 2: "Ventas por efectuar", 3: "Consignaciones", 4: "Entrega gratuita",
  5: "Traslado interno", 6: "Otros traslados no venta", 7: "Guía de devolución", 8: "Traslado para exportación",
  9: "Venta para exportación",
};
const TIPO_DESPACHO = { 1: "Por cuenta del receptor", 2: "Emisor a instalaciones del cliente", 3: "Emisor a otras instalaciones" };
const nuevoId = () => Math.random().toString(36).slice(2, 12);
const ITEM_VACIO = { nombre: "", cantidad: 1, precio: 0 };

function montoDoc(doc, docs) {
  const ref = doc.referencia?.doc ? docs.find((d) => d.id === doc.referencia.doc) : null;
  if (ref && doc.referencia.cod_ref === 2) return 0;
  if (ref && doc.referencia.cod_ref === 1 && !(doc.items || []).length) return montoDoc(ref, docs);
  const exentoDoc = doc.tipo === 34 || ref?.tipo === 34;
  let afecto = 0;
  let exento = 0;
  for (const it of doc.items || []) {
    const m = Math.round(Number(it.cantidad || 0) * Number(it.precio || 0) * (1 - Number(it.descuento_pct || 0) / 100));
    if (exentoDoc || it.exento) exento += m; else afecto += m;
  }
  const dg = Number(doc.descuento_global_pct || 0);
  if (dg > 0 && !(ref && [1, 2].includes(doc.referencia.cod_ref))) {
    afecto = Math.round(afecto * (1 - dg / 100));
    exento = Math.round(exento * (1 - dg / 100));
  }
  return afecto + Math.round(afecto * 0.19) + exento;
}

function foliosSimulacion(docs, cafs) {
  const disponibles = {};
  for (const c of cafs || []) {
    if (c.estado === "activo") disponibles[c.tipo_dte] = (disponibles[c.tipo_dte] || 0) + c.disponibles;
  }
  const requeridos = {};
  for (const d of docs) requeridos[d.tipo] = (requeridos[d.tipo] || 0) + 1;
  return Object.fromEntries(Object.keys(requeridos).sort((a, b) => a - b)
    .map((t) => [t, { requeridos: requeridos[t], disponibles: disponibles[t] || 0 }]));
}

function Simulacion({ st, editable, requisitosOk, onCambio, onBorrador }) {
  const [docs, setDocs] = useState(st.simulacion.documentos);
  const [sucio, setSucio] = useState(false);
  const [error, setError] = useState(st.simulacion.error);
  const [ocupado, setOcupado] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  useEffect(() => {
    setDocs(st.simulacion.documentos);
    setError(st.simulacion.error);
    setSucio(false);
  }, [st.simulacion]);
  useEffect(() => { onBorrador?.(docs); }, [docs]); // eslint-disable-line react-hooks/exhaustive-deps

  const receptores = st.config.receptores || [];
  const cambiarDocs = (nuevos) => { setDocs(nuevos); setSucio(true); };
  const cambiar = (id, cambios) => cambiarDocs(docs.map((d) => (d.id === id ? { ...d, ...cambios } : d)));
  const etiqueta = (d) => `${docs.indexOf(d) + 1} · ${TIPOS[d.tipo]}${d.receptor?.razon_social ? ` · ${d.receptor.razon_social}` : ""}`;

  const agregar = (tipo) => {
    const nota = [56, 61].includes(tipo);
    const base = docs.find((d) => [33, 34].includes(d.tipo));
    cambiarDocs([...docs, {
      id: nuevoId(), tipo,
      receptor: nota ? null : (receptores[0] ? { ...receptores[0] } : { ...VACIO }),
      items: nota ? [] : [{ ...ITEM_VACIO }],
      ...(nota ? { referencia: { doc: base?.id || "", cod_ref: 3, razon: "" } } : {}),
      ...(tipo === 52 ? { ind_traslado: 1 } : {}),
    }]);
  };
  const quitar = (id) => cambiarDocs(docs.filter((d) => d.id !== id).map((d) => (
    d.referencia?.doc === id ? { ...d, referencia: { ...d.referencia, doc: "" } } : d)));
  const mover = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= docs.length) return;
    const n = [...docs];
    [n[i], n[j]] = [n[j], n[i]];
    cambiarDocs(n);
  };

  const ejecutar = async (clave, fn) => {
    setOcupado(clave);
    try {
      await fn();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setOcupado(null);
    }
  };
  const guardar = async (silencioso = false) => {
    const documentos = docs.map((d) => ({
      ...d, items: (d.items || []).map((it) => ({ ...it, cantidad: Number(it.cantidad) || 0, precio: Number(it.precio) || 0 })),
    }));
    const { data } = await api.put("/facturador/certificacion/simulacion", { documentos });
    setSucio(false);
    setError(data.error);
    if (!silencioso) {
      if (data.error) toast.warning(`Guardado, pero falta corregir: ${data.error}`);
      else toast.success("Documentos guardados");
      onCambio();
    }
    return data;
  };
  const cargarEjemplo = () => ejecutar("ejemplo", async () => {
    await api.post("/facturador/certificacion/simulacion/ejemplo");
    toast.success("Se cargaron documentos de ejemplo: ajústelos a sus productos y precios reales");
    onCambio();
  });
  const generar = (prueba) => ejecutar(prueba ? "prueba" : "real", async () => {
    if (sucio) {
      const data = await guardar(true);
      if (data.error) throw new Error(data.error);
    }
    await api.post("/facturador/certificacion/generar", { proceso: "simulacion", prueba });
    toast.success(prueba ? "Vista previa generada" : "Sobre de simulación generado y firmado");
    onCambio();
  });

  const listo = docs.length > 0 && st.config.fch_resol;

  return (
    <Paso n={3} titulo={`Documentos de la simulación (${docs.length})`}
      estado={docs.length && !error ? "ok" : docs.length ? "error" : "pendiente"}
      accion={editable && (
        <div className="flex flex-wrap gap-2 justify-end">
          <button className={BTN_GHOST} disabled={!!ocupado} onClick={() => (docs.length ? setConfirmar("ejemplo") : cargarEjemplo())}>
            {ocupado === "ejemplo" ? "Cargando…" : "Cargar ejemplo"}
          </button>
          <button className={BTN_PRIMARY} disabled={!!ocupado || !sucio} onClick={() => ejecutar("guardar", () => guardar())}>
            {ocupado === "guardar" ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}>
      <div className="space-y-3 text-[13px]">
        <div className="text-[12px] text-muted-foreground">
          Arme entre 10 y 20 documentos que reflejen lo que su empresa emite normalmente, con distintos clientes. Las notas de
          crédito y débito deben referenciar un documento de esta misma lista (toman su receptor). Los folios se asignan al
          generar, en el orden de la lista. Si le falta un cliente, agréguelo en el paso 2.
        </div>
        {error && (
          <div className="text-[12px] text-warning flex gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {docs.length === 0 && (
          <div className="text-muted-foreground">Sin documentos. Agregue uno por tipo o cargue el ejemplo y ajústelo.</div>
        )}
        {docs.map((d, i) => (
          <DocumentoSimulacion key={d.id} d={d} i={i} docs={docs} receptores={receptores} editable={editable}
            etiqueta={etiqueta} onCambio={(c) => cambiar(d.id, c)} onQuitar={() => quitar(d.id)} onMover={(delta) => mover(i, delta)} />
        ))}
        {editable && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] text-muted-foreground">Agregar:</span>
            {TIPOS_SIM.map((t) => (
              <button key={t} className={BTN_GHOST} onClick={() => agregar(t)}><Plus className="h-3.5 w-3.5" /> {TIPOS[t]}</button>
            ))}
          </div>
        )}
        <div className="border-t border-border pt-3 space-y-2">
          <div className="text-[12px] text-muted-foreground">
            Fecha de emisión: <b>{fmtDate(st.fecha_hoy)}</b>. Todos los documentos van en un solo sobre EnvioDTE, sin la
            referencia al set de pruebas.
            {sucio && " Hay cambios sin guardar: se guardan al generar."}
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button className={BTN_GHOST} disabled={!listo || !!ocupado} onClick={() => generar(true)}>
                <FlaskConical className="h-3.5 w-3.5" /> {ocupado === "prueba" ? "Generando…" : "Vista previa (sin certificado)"}
              </button>
              <button className={BTN_PRIMARY} disabled={!listo || !requisitosOk || !!ocupado || !!error}
                onClick={() => setConfirmar("generar")} data-testid="cert-generar-simulacion">
                <ShieldCheck className="h-4 w-4" /> {ocupado === "real" ? "Generando…" : "Generar para enviar al SII"}
              </button>
            </div>
          )}
          {!requisitosOk && docs.length > 0 && (
            <div className="text-[12px] text-muted-foreground">
              Para generar los archivos definitivos necesita el certificado digital y folios de certificación de cada tipo
              usado (paso 1). Si no tiene más folios de un tipo, quite documentos de ese tipo de la lista.
            </div>
          )}
        </div>
      </div>
      <AlertDialog open={!!confirmar} onOpenChange={(o) => !o && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmar === "ejemplo" ? "Reemplazar documentos" : "Generar simulación para el SII"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmar === "ejemplo"
                ? "Los documentos actuales se reemplazarán por el ejemplo."
                : `Se consumirán folios de certificación (${Object.entries(st.folios || {}).map(([t, f]) => `${f.requeridos} del tipo ${t}`).join(", ")}). Los documentos quedan firmados con su certificado y listos para enviar.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const c = confirmar;
              setConfirmar(null);
              if (c === "ejemplo") cargarEjemplo(); else generar(false);
            }}>{confirmar === "ejemplo" ? "Reemplazar" : "Generar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Paso>
  );
}

function DocumentoSimulacion({ d, i, docs, receptores, editable, etiqueta, onCambio, onQuitar, onMover }) {
  const ref = d.referencia || null;
  const refDoc = ref?.doc ? docs.find((x) => x.id === ref.doc) : null;
  const nota = [56, 61].includes(d.tipo);
  const trasladoInterno = d.tipo === 52 && Number(d.ind_traslado) === 5;
  const usaReceptorPropio = !refDoc && !trasladoInterno;
  const sinItems = ref?.doc && Number(ref.cod_ref) === 2;
  const items = d.items || [];
  const setItem = (k, campo, v) => onCambio({ items: items.map((it, j) => (j === k ? { ...it, [campo]: v } : it)) });
  const opcionesRef = docs.filter((x) => x.id !== d.id);
  const rutActual = d.receptor?.rut || "";
  const enLista = receptores.some((r) => r.rut === rutActual);

  return (
    <div className="border border-border rounded-sm p-3 space-y-2.5" data-testid={`sim-doc-${i + 1}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-muted-foreground w-6">{i + 1}.</span>
        <select className={inputAuto} value={d.tipo} disabled={!editable}
          onChange={(e) => {
            const tipo = Number(e.target.value);
            const esNota = [56, 61].includes(tipo);
            onCambio({
              tipo,
              ...(esNota && !d.referencia ? { referencia: { doc: "", cod_ref: 3, razon: "" } } : {}),
              ...(tipo === 52 && !d.ind_traslado ? { ind_traslado: 1 } : {}),
            });
          }}>
          {TIPOS_SIM.map((t) => <option key={t} value={t}>{t} · {TIPOS[t]}</option>)}
        </select>
        <span className="ml-auto font-mono text-[12px] text-muted-foreground">Total aprox. {fmtCLP(montoDoc(d, docs))}</span>
        {editable && (
          <div className="flex items-center gap-1">
            <button className="px-1 text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0}
              onClick={() => onMover(-1)} title="Subir">▲</button>
            <button className="px-1 text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === docs.length - 1}
              onClick={() => onMover(1)} title="Bajar">▼</button>
            <button className="text-muted-foreground hover:text-destructive px-1" onClick={onQuitar} title="Quitar">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {d.tipo === 52 && (
        <div className="grid sm:grid-cols-2 gap-2">
          <select className={inputCls} value={d.ind_traslado || 1} disabled={!editable}
            onChange={(e) => onCambio({ ind_traslado: Number(e.target.value) })}>
            {Object.entries(IND_TRASLADO).map(([k, v]) => <option key={k} value={k}>Traslado: {v}</option>)}
          </select>
          <select className={inputCls} value={d.tipo_despacho || ""} disabled={!editable}
            onChange={(e) => onCambio({ tipo_despacho: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">Despacho: sin indicar</option>
            {Object.entries(TIPO_DESPACHO).map(([k, v]) => <option key={k} value={k}>Despacho: {v}</option>)}
          </select>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="label-caps block mb-1">Receptor</label>
          {usaReceptorPropio ? (
            <select className={inputCls} value={enLista ? rutActual : ""} disabled={!editable}
              onChange={(e) => {
                const r = receptores.find((x) => x.rut === e.target.value);
                if (r) onCambio({ receptor: { ...r } });
              }}>
              <option value="" disabled>{rutActual ? `${d.receptor?.razon_social || ""} (${rutActual})` : "Elija un cliente…"}</option>
              {receptores.map((r) => <option key={r.rut} value={r.rut}>{r.razon_social} · {r.rut}</option>)}
            </select>
          ) : (
            <div className="text-[12px] text-muted-foreground py-2">
              {trasladoInterno ? "La propia empresa (traslado interno)." : `El mismo del documento referenciado (${refDoc.receptor?.razon_social || "—"}).`}
            </div>
          )}
        </div>
        <div>
          <label className="label-caps block mb-1">Referencia {nota ? "(obligatoria)" : "(opcional)"}</label>
          <select className={inputCls} value={ref?.doc || ""} disabled={!editable}
            onChange={(e) => onCambio({ referencia: e.target.value ? { cod_ref: 3, razon: "", ...ref, doc: e.target.value } : (nota ? { ...ref, doc: "" } : undefined) })}>
            <option value="">{nota ? "Elija el documento que corrige…" : "Sin referencia"}</option>
            {opcionesRef.map((x) => <option key={x.id} value={x.id}>{etiqueta(x)}</option>)}
          </select>
        </div>
      </div>

      {ref?.doc && (
        <div className="grid sm:grid-cols-3 gap-2">
          <select className={inputCls} value={ref.cod_ref || 3} disabled={!editable}
            onChange={(e) => onCambio({ referencia: { ...ref, cod_ref: Number(e.target.value) } })}>
            {Object.entries(COD_REF).map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
          </select>
          <input className={`${inputCls} sm:col-span-2`} placeholder="Razón (ej. DEVOLUCION DE MERCADERIA)" maxLength={90}
            value={ref.razon || ""} disabled={!editable}
            onChange={(e) => onCambio({ referencia: { ...ref, razon: e.target.value.toUpperCase() } })} />
        </div>
      )}

      {sinItems ? (
        <div className="text-[12px] text-muted-foreground">
          Corrección de texto: el documento va con monto cero y una línea con la razón indicada.
        </div>
      ) : (
        <div className="space-y-1.5">
          {ref?.doc && Number(ref.cod_ref) === 1 && items.length === 0 && (
            <div className="text-[12px] text-muted-foreground">Sin ítems: se copian los del documento anulado.</div>
          )}
          {items.map((it, k) => (
            <div key={k} className="grid grid-cols-12 gap-1.5 items-center">
              <input className={`${inputCls} col-span-12 sm:col-span-5`} placeholder="Producto o servicio" maxLength={80}
                value={it.nombre} disabled={!editable} onChange={(e) => setItem(k, "nombre", e.target.value)} />
              <input type="number" min="0" step="any" className={`${inputCls} col-span-3 sm:col-span-2 font-mono`} placeholder="Cant."
                value={it.cantidad} disabled={!editable} onChange={(e) => setItem(k, "cantidad", e.target.value === "" ? "" : Number(e.target.value))} />
              <input type="number" min="0" step="any" className={`${inputCls} col-span-4 sm:col-span-2 font-mono`} placeholder="Precio neto"
                value={it.precio} disabled={!editable} onChange={(e) => setItem(k, "precio", e.target.value === "" ? "" : Number(e.target.value))} />
              <input type="number" min="0" max="99" step="any" className={`${inputCls} col-span-3 sm:col-span-1 font-mono`} placeholder="Desc%"
                value={it.descuento_pct ?? ""} disabled={!editable}
                onChange={(e) => setItem(k, "descuento_pct", e.target.value === "" ? undefined : Number(e.target.value))} />
              <label className="col-span-1 text-[11px] flex items-center gap-1" title="Ítem exento de IVA">
                {d.tipo === 33 && !refDoc && (
                  <><input type="checkbox" checked={!!it.exento} disabled={!editable}
                    onChange={(e) => setItem(k, "exento", e.target.checked)} /> Ex.</>
                )}
              </label>
              {editable && (
                <button className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center"
                  onClick={() => onCambio({ items: items.filter((_, j) => j !== k) })}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            {editable && (
              <button className={BTN_GHOST} onClick={() => onCambio({ items: [...items, { ...ITEM_VACIO }] })}>
                <Plus className="h-3.5 w-3.5" /> Ítem
              </button>
            )}
            {[33, 34].includes(d.tipo) && !(ref?.doc && [1, 2].includes(Number(ref.cod_ref))) && (
              <label className="text-[12px] text-muted-foreground inline-flex items-center gap-1.5">
                Descuento global %
                <input type="number" min="0" max="99" step="any" className={`${inputCls.replace("w-full", "w-20")} font-mono`}
                  value={d.descuento_global_pct ?? ""} disabled={!editable}
                  onChange={(e) => onCambio({ descuento_global_pct: e.target.value === "" ? undefined : Number(e.target.value) })} />
              </label>
            )}
            <span className="text-[11px] text-muted-foreground">Precios netos (sin IVA).</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SiguientesPasosSimulacion() {
  return (
    <section className={`${CARD} p-4 sm:p-5 text-[13px] leading-relaxed`}>
      <div className="font-semibold mb-2">Después de enviar</div>
      <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
        <li>Envíe el sobre a Maullín y consulte el estado hasta ver <b>EPR</b> sin rechazos ni reparos. Si algún documento
          tiene reparos, corríjalo aquí y genere de nuevo (usa folios nuevos).</li>
        <li>En el{" "}
          <a href={URL_POSTULANTES} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5 text-foreground">
            menú de postulantes <ExternalLink className="h-3 w-3" />
          </a>{" "}
          declare el avance de la <b>Simulación</b> con el TrackID del sobre. El resultado llega por correo.</li>
        <li>Luego siguen el intercambio de información y las <b>muestras impresas</b>: descargue el .zip de esta generación;
          en la carpeta <i>muestras</i> está cada documento (tributario y cedible). Súbalos junto a los del set de pruebas en
          "Upload de muestras impresas" (un PDF por documento, una página).</li>
      </ol>
      <a href={URL_INSTRUCTIVO} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline mt-2">
        Instructivo del proceso de certificación (SII) <ExternalLink className="h-3 w-3" />
      </a>
    </section>
  );
}

// ---------------------------------------------------------------------------
function EstadoSII({ a, rutCert }) {
  const e = a.estado_sii;
  if (!a.track_id) return <span className="text-muted-foreground">No enviado</span>;
  if (!e) return <span className="font-mono">TrackID {a.track_id}</span>;
  const sinPermiso = esPermiso(e);
  const malo = sinPermiso || /^(R|LR|LN)/.test(e.estado || "");
  const bueno = ["EPR", "LOK"].includes(e.estado);
  const reparos = (e.detalle || []).reduce((s, d) => s + Number(d.REPAROS || 0) + Number(d.RECHAZADOS || 0), 0);
  return (
    <div>
      <div className="font-mono">TrackID {a.track_id}{a.metodo === "api" ? " · API boletas" : ""}</div>
      <div className={malo ? "text-destructive" : bueno && !reparos ? "text-success" : "text-warning"}>
        {e.estado} · {e.descripcion || e.glosa}
        {bueno && reparos > 0 && ` · ${reparos} con reparos/rechazos`}
      </div>
      {e.glosa && e.descripcion && e.glosa.toLowerCase() !== e.descripcion.toLowerCase() && (
        <div className="text-[11px] text-muted-foreground text-left max-w-md">SII: {e.glosa}</div>
      )}
      {e.num_atencion && <div className="text-[11px] text-muted-foreground">N° atención {e.num_atencion}</div>}
      {!sinPermiso && ["RCT", "LRC"].includes(e.estado) && (
        <div className="text-[11px] text-left max-w-sm mt-1 text-muted-foreground">
          Error en la carátula: casi siempre la fecha de resolución no coincide con la que el SII tiene para la empresa
          en certificación (el número debe ser 0). Revísela en{" "}
          <a href={URL_USUARIOS} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
            Maullín → Consulta entre Empresas Autorizados <ExternalLink className="h-3 w-3" />
          </a>
          , corríjala en «Datos para los documentos», elimine esta generación y genere de nuevo. El detalle exacto llega al
          correo de contacto registrado en Maullín.
        </div>
      )}
      {["RSC", "LRS", "RCH", "LRH", "LNC"].includes(e.estado) && (
        <div className="text-[11px] text-left max-w-sm mt-1 text-muted-foreground">
          El detalle del rechazo (línea y campo) llega al correo de contacto de la empresa registrado en Maullín.
        </div>
      )}
      {sinPermiso && (
        <div className="text-[11px] text-left max-w-sm mt-1 text-muted-foreground">
          El SII recibió el archivo, pero el titular del certificado{rutCert ? <> (RUT <b className="font-mono">{rutCert}</b>)</> : ""} no
          está autorizado a enviar documentos de esta empresa en certificación. El usuario administrador debe entrar a{" "}
          <a href={URL_USUARIOS} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
            Maullín → Mantención de Usuarios Autorizados <ExternalLink className="h-3 w-3" />
          </a>
          , ingresar el RUT de la empresa y darle a ese RUT los perfiles «Firmar Doctos» y «Enviar Doctos». Luego
          presione «Reenviar»: se usa el mismo archivo, sin gastar folios.
        </div>
      )}
      {(e.detalle || []).map((d, i) => (
        <div key={i} className="text-[11px] text-muted-foreground font-mono">
          Tipo {d.TIPO_DOCTO}: informados {d.INFORMADOS}, aceptados {d.ACEPTADOS}, rechazados {d.RECHAZADOS}, reparos {d.REPAROS}
        </div>
      ))}
      {(e.errores || []).slice(0, 8).map((er, i) => (
        <div key={`er-${i}`} className="text-[11px] text-destructive text-left max-w-md">{er}</div>
      ))}
    </div>
  );
}

function Generaciones({ st, editable, onCambio, manual = false }) {
  const [abierta, setAbierta] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [borrar, setBorrar] = useState(null);

  const accion = async (clave, fn) => {
    setOcupado(clave);
    try {
      await fn();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setOcupado(null);
    }
  };

  const enviar = (a, metodo = "upload") => accion(`env-${a.id}-${metodo}`, async () => {
    const { data } = await api.post(`/facturador/certificacion/archivos/${a.id}/enviar`, { metodo });
    toast.success(`Enviado al SII. TrackID ${data.track_id}`);
    onCambio();
  });
  const consultar = (a) => accion(`est-${a.id}`, async () => {
    await api.get(`/facturador/certificacion/archivos/${a.id}/estado`);
    onCambio();
  });

  const n = manual ? 3 : 5;
  const titulo = manual ? "Archivos de respuesta" : "Archivos generados y envío";
  if (!st.generaciones.length) {
    return (
      <Paso n={n} titulo={titulo} estado="pendiente">
        <p className="text-[13px] text-muted-foreground">Aún no hay generaciones.</p>
      </Paso>
    );
  }

  return (
    <Paso n={n} titulo={titulo}
      estado={st.generaciones.some((g) => !g.prueba && (manual || g.archivos.every((a) => a.track_id
        && !REENVIABLES.includes(a.estado_sii?.estado)))) ? "ok" : "pendiente"}>
      <div className="space-y-3">
        {st.generaciones.map((g, gi) => (
          <div key={g.id} className="border border-border rounded-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-muted/40">
              <div className="text-[13px] flex items-center gap-2">
                {g.prueba
                  ? <span className="text-[11px] px-1.5 py-0.5 rounded-sm bg-warning/15 text-warning font-medium">VISTA PREVIA</span>
                  : <span className="text-[11px] px-1.5 py-0.5 rounded-sm bg-success/15 text-success font-medium">PARA ENVIAR</span>}
                <span>{fmtDate(g.created_at)}</span>
                {!g.prueba && Object.keys(g.folios_usados || {}).length > 0 && (
                  <span className="text-muted-foreground text-[12px]">
                    Folios: {Object.entries(g.folios_usados).map(([t, [a, b]]) => `${t}: ${a}–${b}`).join(" · ")}
                  </span>
                )}
                {gi > 0 && <span className="text-[11px] text-muted-foreground">(anterior)</span>}
              </div>
              <div className="flex gap-2">
                {g.tiene_documentos && (
                  <button className={BTN_GHOST} onClick={() => accion(`pdf-${g.id}`, () => abrirBlob(`/facturador/certificacion/generaciones/${g.id}/pdf`))}>
                    <FileText className="h-3.5 w-3.5" /> {ocupado === `pdf-${g.id}` ? "Generando…" : "Muestras PDF"}
                  </button>
                )}
                <button className={BTN_GHOST} onClick={() => accion(`zip-${g.id}`, () => descargarBlob(`/facturador/certificacion/generaciones/${g.id}/zip`, `${{ boleta: "boletas_", simulacion: "simulacion_", intercambio: "intercambio_" }[g.proceso] || ""}certificacion_${g.fecha}.zip`))}>
                  <Package className="h-3.5 w-3.5" /> {ocupado === `zip-${g.id}` ? "Preparando…" : "Todo (.zip)"}
                </button>
                {editable && borrable(g) && (
                  <button className="text-muted-foreground hover:text-destructive px-1" onClick={() => setBorrar(g)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-border">
              {g.archivos.map((a) => {
                const valido = a.errores_xsd.length === 0 && a.firma_ok;
                return (
                  <div key={a.id} className="px-3 py-2 text-[13px]">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-1.5">
                          {a.documentos?.length > 0 && (
                            <button onClick={() => setAbierta(abierta === a.id ? null : a.id)}>
                              {abierta === a.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          )}
                          {a.nombre}
                        </div>
                        <div className="text-[12px] text-muted-foreground font-mono">{a.archivo}</div>
                        <div className={`text-[12px] flex items-center gap-1 ${valido ? "text-success" : "text-destructive"}`}>
                          {valido ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                          {valido ? "Esquema SII y firma válidos" : "Con errores de validación"}
                          {a.folio_notificacion ? <span className="text-muted-foreground ml-1">· Folio notificación {a.folio_notificacion}</span> : null}
                          {a.tipo === "rcof" && a.sec_envio ? <span className="text-muted-foreground ml-1">· Día {fmtDate(a.fecha)} · Secuencia de envío {a.sec_envio}</span> : null}
                        </div>
                        {a.errores_xsd.map((er) => <div key={er} className="text-[11px] text-destructive font-mono">{er}</div>)}
                      </div>
                      <div className="ml-auto text-[12px] text-right">
                        {manual
                          ? <span className="text-muted-foreground">{g.prueba ? "Solo de muestra" : "Se sube en el sitio de postulantes del SII"}</span>
                          : <EstadoSII a={a} rutCert={st.certificado?.rut} />}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button className={BTN_GHOST} onClick={() => accion(`xml-${a.id}`, () => descargarBlob(`/facturador/certificacion/archivos/${a.id}/xml`, a.archivo))}>
                        <FileCode className="h-3.5 w-3.5" /> XML
                      </button>
                      {editable && !manual && !g.prueba && !a.track_id && (
                        <button className={BTN_PRIMARY} disabled={!valido || !!ocupado} onClick={() => enviar(a)}>
                          <Send className="h-3.5 w-3.5" /> {ocupado === `env-${a.id}-upload` ? "Enviando…" : "Enviar a Maullín"}
                        </button>
                      )}
                      {editable && !g.prueba && !a.track_id && a.tipo === "envio_boleta" && (
                        <button className={BTN_GHOST} disabled={!valido || !!ocupado} onClick={() => enviar(a, "api")}
                          title="Alternativa: API REST de boletas del SII (ambiente de certificación)">
                          <Send className="h-3.5 w-3.5" /> {ocupado === `env-${a.id}-api` ? "Enviando…" : "Enviar por API de boletas"}
                        </button>
                      )}
                      {editable && !g.prueba && a.track_id && a.tipo !== "rcof" && REENVIABLES.includes(a.estado_sii?.estado) && esPermiso(a.estado_sii) && (
                        <button className={BTN_PRIMARY} disabled={!valido || !!ocupado} onClick={() => enviar(a, a.metodo || "upload")}>
                          <Send className="h-3.5 w-3.5" /> {ocupado?.startsWith(`env-${a.id}`) ? "Enviando…" : "Reenviar"}
                        </button>
                      )}
                      {a.track_id && (
                        <button className={BTN_GHOST} disabled={!!ocupado} onClick={() => consultar(a)}>
                          <RefreshCw className={`h-3.5 w-3.5 ${ocupado === `est-${a.id}` ? "animate-spin" : ""}`} /> Consultar estado
                        </button>
                      )}
                    </div>
                    {a.tipo === "rcof" && a.track_id && !g.prueba && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Un RCOF no se reenvía: si el SII lo rechaza o repara, genere uno nuevo con «Generar solo RCOF» para el
                        día {fmtDate(a.fecha)} (sale con una secuencia mayor).
                      </div>
                    )}
                    {abierta === a.id && (
                      <table className="w-full text-[12px] mt-2">
                        <thead className="text-muted-foreground">
                          <tr>
                            <th className="text-left py-1">Caso</th><th className="text-left">Documento</th><th className="text-left">Folio</th>
                            <th className="text-left">Receptor</th><th className="text-right">Total</th><th className="text-right">PDF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.documentos.map((d) => (
                            <tr key={d.caso} className="border-t border-border/60">
                              <td className="py-1 font-mono">{d.caso}</td>
                              <td>{TIPOS[d.tipo_dte] || d.tipo_dte}</td>
                              <td className="font-mono">{d.folio}</td>
                              <td className="truncate max-w-[200px]">{d.receptor}</td>
                              <td className="text-right font-mono">{fmtCLP(d.total)}</td>
                              <td className="text-right whitespace-nowrap">
                                <button className="underline mr-2" onClick={() => accion(`p-${d.caso}`, () => abrirBlob(`/facturador/certificacion/generaciones/${g.id}/documentos/${d.caso}/pdf`))}>
                                  {[39, 41].includes(d.tipo_dte) ? "Ver boleta" : "Tributario"}
                                </button>
                                {(d.cedible ?? [33, 34, 46].includes(d.tipo_dte)) && (
                                  <button className="underline" onClick={() => accion(`c-${d.caso}`, () => abrirBlob(`/facturador/certificacion/generaciones/${g.id}/documentos/${d.caso}/pdf?cedible=true`))}>Cedible</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={!!borrar} onOpenChange={(o) => !o && setBorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar generación</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán los archivos de esta generación.{" "}
              {borrar && !borrar.prueba && !manual && "Como el SII no la recibió o la rechazó completa, sus folios vuelven a quedar disponibles si fueron los últimos usados. Luego genere de nuevo."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => accion("borrar", async () => {
              const { data } = await api.delete(`/facturador/certificacion/generaciones/${borrar.id}`);
              const dev = Object.entries(data.folios_devueltos || {});
              if (dev.length) toast.success(`Folios recuperados: ${dev.map(([t, [a, b]]) => `${t}: ${a}–${b}`).join(" · ")}`);
              setBorrar(null);
              onCambio();
            })}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Paso>
  );
}

// ---------------------------------------------------------------------------
const ESTADOS_COMERCIAL = { 0: "DTE Aceptado OK", 1: "DTE Aceptado con Discrepancias", 2: "DTE Rechazado" };

function Chequeo({ ok, texto }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? "text-success" : "text-destructive"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {texto}
    </span>
  );
}

function Intercambio({ st, editable, onCambio, onIrConfig }) {
  const inter = st.intercambio;
  const [subiendo, setSubiendo] = useState(false);
  const [generando, setGenerando] = useState(null);
  const [decisiones, setDecisiones] = useState(inter?.decisiones || []);
  const [recinto, setRecinto] = useState(inter?.recinto || st.recinto_sugerido || "");

  useEffect(() => {
    setDecisiones(st.intercambio?.decisiones || []);
    setRecinto(st.intercambio?.recinto || st.recinto_sugerido || "");
  }, [st.intercambio, st.recinto_sugerido]);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facturador/certificacion/intercambio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Envío cargado: ${data.intercambio.documentos.length} documentos`);
      onCambio();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubiendo(false);
    }
  };

  const cambiar = (i, cambios) => setDecisiones(decisiones.map((d, j) => (j === i ? { ...d, ...cambios } : d)));

  const generar = async (prueba) => {
    setGenerando(prueba ? "prueba" : "real");
    try {
      await api.post("/facturador/certificacion/intercambio/generar", { prueba, recinto, documentos: decisiones });
      toast.success(prueba ? "Vista previa generada" : "Respuestas generadas y firmadas");
      onCambio();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGenerando(null);
    }
  };

  const aceptados = decisiones.filter((d) => d.estado_dte !== 2).length;
  const accionSubir = editable && (
    <label className={`${BTN_GHOST} cursor-pointer`}>
      <Upload className="h-3.5 w-3.5" /> {subiendo ? "Cargando…" : inter ? "Reemplazar envío" : "Subir XML del SII"}
      <input type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={subir} disabled={subiendo}
        data-testid="cert-intercambio-subir" />
    </label>
  );

  return (
    <>
      <Paso n={1} titulo="Envío recibido del SII" accion={accionSubir}
        estado={!inter ? "pendiente" : inter.estado === 0 ? "ok" : "error"}>
        {!inter ? (
          <p className="text-[13px] text-muted-foreground">
            En el{" "}
            <a href={URL_POSTULANTES} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
              menú de postulantes del SII <ExternalLink className="h-3 w-3" />
            </a>{" "}
            descargue el archivo XML del intercambio (un EnvioDTE dirigido al RUT de la empresa
            {st.emisor.rut ? <b className="font-mono"> {st.emisor.rut}</b> : ""}) y súbalo aquí tal cual.
          </p>
        ) : (
          <div className="space-y-3 text-[13px]" data-testid="cert-intercambio-envio">
            <div className="text-muted-foreground">
              <span className="font-mono">{inter.nombre}</span> · cargado {fmtDate(inter.cargado_at)} · emisor{" "}
              <span className="font-mono">{inter.rut_emisor}</span> · receptor <span className="font-mono">{inter.rut_receptor}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
              <Chequeo ok={inter.errores_xsd.length === 0} texto="Esquema EnvioDTE" />
              <Chequeo ok={inter.firma_ok} texto="Firma del envío" />
              <Chequeo ok={inter.dirigido_empresa} texto="Dirigido a la empresa" />
            </div>
            <div className={`text-[13px] font-medium ${inter.estado === 0 ? "text-success" : "text-destructive"}`}>
              Estado de recepción del envío: {inter.estado} · {inter.glosa}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left py-1">Documento</th><th className="text-left">Emisión</th>
                    <th className="text-left">Receptor</th><th className="text-right">Total</th>
                    <th className="text-left pl-3">Recepción</th><th className="text-left pl-3">Aceptación comercial</th>
                  </tr>
                </thead>
                <tbody>
                  {inter.documentos.map((d, i) => {
                    const dec = decisiones[i] || {};
                    return (
                      <React.Fragment key={`${d.tipo}-${d.folio}-${i}`}>
                        <tr className="border-t border-border/60 align-top" data-testid={`cert-intercambio-doc-${d.folio}`}>
                          <td className="py-1.5">{TIPOS[d.tipo] || `Tipo ${d.tipo}`} <span className="font-mono">N° {d.folio}</span></td>
                          <td className="whitespace-nowrap">{fmtDate(d.fch_emis)}</td>
                          <td>
                            <div className="font-mono">{d.rut_recep}</div>
                            <div className="text-muted-foreground truncate max-w-[180px]">{d.razon_social_recep}</div>
                          </td>
                          <td className="text-right font-mono">{fmtCLP(d.total)}</td>
                          <td className={`pl-3 ${d.estado_recep === 0 ? "text-success" : "text-destructive"}`}>
                            {d.estado_recep} · {d.glosa_recep}
                          </td>
                          <td className="pl-3 space-y-1 min-w-[230px]">
                            <select className={inputCls} value={dec.estado_dte ?? 0} disabled={!editable}
                              onChange={(e) => cambiar(i, { estado_dte: Number(e.target.value), glosa_dte: ESTADOS_COMERCIAL[e.target.value] })}>
                              <option value={0}>Aceptar</option>
                              <option value={1}>Aceptar con discrepancias</option>
                              <option value={2}>Rechazar</option>
                            </select>
                            <input className={inputCls} value={dec.glosa_dte || ""} maxLength={256} disabled={!editable}
                              onChange={(e) => cambiar(i, { glosa_dte: e.target.value })} placeholder="Glosa de la respuesta" />
                          </td>
                        </tr>
                        {d.observaciones.length > 0 && (
                          <tr>
                            <td colSpan={6} className="pb-1.5 text-[11px] text-warning">
                              {d.observaciones.map((o) => <div key={o} className="flex gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {o}</div>)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Se propone aceptar los documentos recibidos conforme y rechazar los demás (por ejemplo, los dirigidos a otro
              RUT). Los envíos de intercambio del SII traen timbres de prueba: esa observación no impide aceptarlos.
            </p>
          </div>
        )}
      </Paso>

      <Paso n={2} titulo="Generar respuestas firmadas" estado={st.generaciones.some((g) => !g.prueba) ? "ok" : "pendiente"}>
        <div className="space-y-3 text-[13px]">
          <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
            <li><b>Respuesta de intercambio:</b> acuse de recibo del envío y estado de cada documento.</li>
            <li><b>Recibo de mercaderías o servicios</b> (Ley 19.983): solo de las facturas y guías aceptadas
              {inter ? ` (${aceptados} aceptado${aceptados === 1 ? "" : "s"})` : ""}; si no acepta ninguno, no se genera.</li>
            <li><b>Aprobación comercial:</b> la aceptación o el rechazo de cada documento, con su glosa.</li>
          </ul>
          <div className="max-w-md">
            <label className="text-[12px] text-muted-foreground">Recinto (lugar donde se reciben las mercaderías o servicios)</label>
            <input className={inputCls} value={recinto} maxLength={80} disabled={!editable} onChange={(e) => setRecinto(e.target.value)}
              placeholder="Ej.: Oficina central" data-testid="cert-intercambio-recinto" />
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button className={BTN_GHOST} disabled={!inter || !recinto.trim() || !!generando} onClick={() => generar(true)}>
                <FlaskConical className="h-3.5 w-3.5" /> {generando === "prueba" ? "Generando…" : "Vista previa (sin certificado)"}
              </button>
              <button className={BTN_PRIMARY} disabled={!inter || !recinto.trim() || !st.certificado.configurado || !!generando}
                onClick={() => generar(false)} data-testid="cert-intercambio-generar">
                <ShieldCheck className="h-4 w-4" /> {generando === "real" ? "Generando…" : "Generar firmadas"}
              </button>
            </div>
          )}
          {!st.certificado.configurado && (
            <div className="text-[12px] text-muted-foreground">
              Para firmar las respuestas necesita el certificado digital de la empresa.{" "}
              {onIrConfig && <button className="underline" onClick={onIrConfig}>Ir a la configuración del facturador</button>}
            </div>
          )}
          {st.certificado.configurado && st.certificado.rut && (
            <div className="text-[12px] text-muted-foreground">
              Firma el titular del certificado, RUT <b className="font-mono">{st.certificado.rut}</b> (va en el recibo de mercaderías).
            </div>
          )}
        </div>
      </Paso>
    </>
  );
}

function SiguientesPasosIntercambio() {
  return (
    <section className={`${CARD} p-4 sm:p-5 text-[13px] leading-relaxed`}>
      <div className="font-semibold mb-2">Después de generar</div>
      <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
        <li>Descargue los archivos de la generación <b>PARA ENVIAR</b> (botón XML de cada uno o «Todo (.zip)»).</li>
        <li>En el{" "}
          <a href={URL_POSTULANTES} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5 text-foreground">
            menú de postulantes <ExternalLink className="h-3 w-3" />
          </a>
          , entre a «Declarar avance de la postulación: subir archivos XML de respuesta de intercambio» y suba cada archivo
          en su casilla: 1) respuesta de intercambio, 2) recibo de mercaderías, 3) aprobación comercial.</li>
        <li>El resultado de la revisión llega al correo de contacto registrado en el SII. Si pide correcciones, ajuste la
          aceptación aquí y genere de nuevo: no consume folios.</li>
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
function SiguientesPasos() {
  return (
    <section className={`${CARD} p-4 sm:p-5 text-[13px] leading-relaxed`}>
      <div className="font-semibold mb-2">Después de enviar</div>
      <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
        <li>Consulte el estado hasta ver <b>EPR</b> (sobre procesado) y <b>LOK</b> (libros). Un documento con reparos se
          vuelve a generar: usa folios nuevos automáticamente.</li>
        <li>Los libros se revisan contra los sets ya aprobados: si el correo dice "No tiene un SET Guía de Despacho
          aprobado", primero debe aprobarse el sobre de documentos y luego se reenvía el libro.</li>
        <li>En Maullín, en <i>Set de pruebas → Declarar avance</i>, indique los TrackID del sobre y de cada libro. El
          veredicto real llega por correo (revisión del SII).</li>
        <li>Luego siguen la simulación, el intercambio de información y las <b>muestras impresas</b>: descargue el .zip de
          la generación aprobada, descomprímalo y suba uno a uno los PDF de la carpeta <i>muestras</i> en{" "}
          <a href={URL_POSTULANTES} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5 text-foreground">
            Menú Postulantes → Upload de muestras impresas <ExternalLink className="h-3 w-3" />
          </a>. Agregue un documento de cada tipo de la simulación (PDF y "Cedible" desde el facturador).</li>
      </ol>
      <a href={URL_INSTRUCTIVO} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline mt-2">
        Instructivo del proceso de certificación (SII) <ExternalLink className="h-3 w-3" />
      </a>
    </section>
  );
}

function SiguientesPasosBoleta() {
  const enlace = (href, texto) => (
    <a href={href} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5 text-foreground">
      {texto} <ExternalLink className="h-3 w-3" />
    </a>
  );
  return (
    <section className={`${CARD} p-4 sm:p-5 text-[13px] leading-relaxed`}>
      <div className="font-semibold mb-2">Después de enviar</div>
      <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
        <li>Envíe el sobre de boletas y consulte su estado (el SII recomienda esperar unos 10 minutos) hasta ver <b>EPR</b>
          sin rechazos ni reparos. Si alguna boleta tiene reparos, vuelva a generar: se usan folios nuevos.</li>
        <li>Envíe el <b>Reporte de Consumo de Folios</b> del mismo día y consulte su estado.</li>
        <li>Ingrese a la {enlace(URL_BOL_REVISION, "Solicitud de Revisión")} e informe el TrackID del envío de boletas.
          El resultado de la revisión llega por correo.</li>
        <li>Con el V°B° del SII, el representante legal realiza la {enlace(URL_BOL_DECLARACION, "Declaración de Cumplimiento")}.
          Queda autorizado para emitir boletas electrónicas a contar del primer día del mes en curso.</li>
        <li>En producción, cambie el ambiente a producción en la configuración del facturador: las boletas se envían por la
          API de boletas del SII y se imprimen en formato ticket (botón "Muestras PDF").</li>
      </ol>
      <a href={URL_BOL_GUIA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline mt-2">
        Instructivo de certificación de boletas electrónicas (SII) <ExternalLink className="h-3 w-3" />
      </a>
    </section>
  );
}
